import { ConditionOperator as ConditionOperatorEnum } from "@sinch/core/entities/serverEnums";
import { ConditionOperator as ConditionOperatorType } from "@sinch/core/entities/serverEnums.types";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useEntityProps } from "../utilsHooks";

type SetType = "none" | "single" | "multiple" | "range";

/**
 * Single source of truth for how a condition operator looks in the app: label and description.
 *
 * Use these getters wherever a condition operator is shown.
 * Never hardcode its label and description in a component, change them here.
 * Every value is shown in the "Entity Identity/ConditionOperator" story.
 */
export const useOperator = () => {
  const { t } = useTranslation();

  const operators: Record<ConditionOperatorType, { label: string; setType: SetType; description: string }> = {
    [ConditionOperatorEnum.Equal]: { label: "=", description: t("Operator.equalsDescription"), setType: "single" },
    [ConditionOperatorEnum.GreaterThan]: {
      label: ">",
      description: t("Operator.greaterThanDescription"),
      setType: "single",
    },
    [ConditionOperatorEnum.GreaterThanOrEqual]: {
      label: "≥",
      description: t("Operator.greaterThanOrEqualDescription"),
      setType: "single",
    },
    [ConditionOperatorEnum.LessThan]: { label: "<", description: t("Operator.lessThanDescription"), setType: "single" },
    [ConditionOperatorEnum.LessThanOrEqual]: {
      label: "≤",
      description: t("Operator.lessThanOrEqualDescription"),
      setType: "single",
    },
    [ConditionOperatorEnum.NotEqual]: {
      label: "≠",
      description: t("Operator.notEqualsDescription"),
      setType: "single",
    },

    [ConditionOperatorEnum.In]: {
      label: t("Operator.in"),
      description: t("Operator.inDescription"),
      setType: "multiple",
    },
    [ConditionOperatorEnum.NotIn]: {
      label: t("Operator.notIn"),
      description: t("Operator.notInDescription"),
      setType: "multiple",
    },

    [ConditionOperatorEnum.NotFilled]: {
      label: t("Operator.notFilled"),
      description: t("Operator.notFilledDescription"),
      setType: "none",
    },

    [ConditionOperatorEnum.Exist]: {
      label: t("Operator.exist"),
      description: t("Operator.existDescription"),
      setType: "none",
    },
    [ConditionOperatorEnum.NotExist]: {
      label: t("Operator.notExists"),
      description: t("Operator.notExistsDescription"),
      setType: "none",
    },
  };
  const getProp = useEntityProps(operators);

  const getOperatorLabel = getProp<string>("label");
  const getOperatorSetType = getProp<SetType>("setType");
  const getOperatorDescription = useMemo(() => getProp<string>("description"), [getProp]);

  return { getOperatorLabel, getOperatorSetType, getOperatorDescription };
};
